import { readableLabel, readableValue } from './readable';

/**
 * Plain-English text for the audit log: what an action was, whether it was
 * only a page being opened, and its detail as named fields instead of JSON.
 */

const LABELS: Record<string, string> = {
  'session.sign_in': 'Signed in',
  'session.sign_out': 'Signed out',
  'session.refused': 'A request was refused',
  'session.me': 'Opened the portal',
  'auth.request_link': 'Asked for a sign-in link',
  'auth.setup_link': 'A sign-in link was issued',
  'session.expired': 'A session ended from idleness',
  'staff.bootstrap': 'The first super admin was created',
  'staff.login_created': "A staff member's login was created",
  'changes.applied': 'An approved change was applied',
  'cases.change_customer': 'Changed the customer on a case',
  'customers.view': 'Opened a customer tab',
  'transactions.list': 'Viewed the live transactions',
  'exports.scheduled': 'A scheduled export ran',
  'retention.run': 'The retention job ran',
  'verifications.attempts': 'Viewed the verification attempts log',
  'staff.add': 'Added a staff member',
  'staff.disable': 'Disabled a staff account',
  'staff.enable': 'Turned a staff account back on',
  'staff.make_super_admin': 'Made a staff member a super admin',
  'staff.set_roles': "Changed a staff member's roles",
  'staff.set_override': "Changed a staff member's permission",
  'staff.resend_invite': 'Resent a sign-in link',
  'staff.reset_mfa': "Reset a staff member's authenticator",
  'staff.link_app': "Linked a staff member's app account",
  'cells.access': "Viewed a staff member's access in other countries",
  'cells.invite': 'Invited a staff member in another country',
  'cells.invite_sent': 'The invitation reached the other country',
  'cells.invite_failed': 'The invitation did not reach the other country',
  'cells.remove': 'Removed a staff member from another country',
  'cells.remove_sent': 'The removal reached the other country',
  'cells.remove_failed': 'The removal did not reach the other country',
  'countries.open': 'Opened the country picker',
  'countries.list': 'Viewed the countries',
  'countries.switch': 'Switched a country on or off',
  'countries.listed': 'Listed a country in the app and the portal',
  'countries.unlisted': 'Took a country off the list',
  'countries.market_sent': "The country's market switch was set",
  'countries.market_failed': "The country's market switch could not be set",
  'hq.status': 'Headquarters asked about a staff member or the country',
  'hq.market': "Headquarters set this country's market switch",
  'hq.invite': 'Headquarters made a staff member staff here',
  'hq.remove': 'Headquarters turned a staff member off here',
  'staff.list': 'Viewed the staff list',
  'roles.catalogue': 'Viewed the roles',
  'audit.list': 'Viewed the audit log',
  'audit.verify': 'Checked the audit chain',
  'changes.propose': 'Proposed a change',
  'changes.approve': 'Approved a change',
  'changes.reject': 'Rejected a change',
  'changes.withdraw': 'Withdrew a change',
  'changes.get': 'Opened a change request',
  'changes.list': 'Viewed change requests',
  'customers.search': 'Searched customers',
  'customers.get': 'Opened a customer',
  'customers.tab': 'Opened a customer tab',
  'customers.statement': "Opened a customer's statement",
  'customers.reveal_phone': 'Revealed a phone number',
  'customers.reveal_identity': 'Revealed an identity',
  'transactions.get': 'Opened a transaction',
  'issues.list': 'Viewed issue reports',
  'issues.get': 'Opened an issue report',
  'issues.assign': 'Assigned an issue report',
  'issues.set_status': 'Changed an issue report status',
  'issues.add_note': 'Added a note to an issue report',
  'issues.attach': 'Attached a file to an issue report',
  'credits.lines': 'Viewed the unmatched pool statement lines',
  'credits.attach': 'Attached a bank statement to a credit',
  'credits.file_link': 'Opened a bank statement on a credit',
  'issues.workers': 'Viewed who can work reports',
  'cases.open': 'Opened a case',
  'cases.get': 'Opened a case',
  'cases.list': 'Viewed cases',
  'cases.claim': 'Claimed a case',
  'cases.update': 'Updated a case',
  'cases.close': 'Closed a case',
  'breaks.close': 'Closed a break alone',
  'breaks.close_any': 'Closed a break alone (super admin)',
  'flags.set': 'Changed a feature flag',
  'flags.list': 'Viewed feature flags',
  'settings.set': 'Changed a setting',
  'settings.list': 'Viewed settings',
  'limits.list': 'Viewed limits',
  'fees.list': 'Viewed fees',
  'fees.preview': 'Previewed a fee',
  'notices.list': 'Viewed notices',
  'notices.end': 'Ended a notice',
  'notices.archive': 'Archived a notice',
  'messages.send': 'Sent a message',
  'messages.deliveries': 'Viewed message deliveries',
  'messages.templates': 'Viewed message templates',
  'messages.segment_count': 'Counted a message audience',
  'exports.create': 'Started an export',
  'exports.list': 'Viewed exports',
  'exports.link': 'Downloaded an export',
  'exports.schedule': 'Scheduled an export',
  'exports.unschedule': 'Stopped a scheduled export',
  'exports.schedules': 'Viewed scheduled exports',
  'alerts.mine': 'Viewed own alerts',
  'alerts.set_mine': 'Changed own alerts',
  'ops.status': 'Opened the status board',
  'wall.payments': 'Read payments for a wall',
  'wall.providers': 'Read provider health for a wall',
  'wall.services': 'Read service health for a wall',
  'wall.security': 'Read account security counts for a wall',
  'kyc.list': 'Opened the verifications in review',
  'kyc.detail': "Opened a verification's details",
  'kyc.decide': 'Decided an identity verification',
  'kyc.decide_sent': 'Sent a verification decision to Didit',
  'kyc.retire_didit': 'Closed the Didit reviews when Didit was retired',
  'referrals.list': 'Opened the referrals',
  'referrals.referrer': "Opened a person's referrals",
  'referrals.tree': 'Opened the referral tree',
  'referrals.void': 'Voided a referral',
  'killswitch.off': 'Turned a kill switch off',
  'wallets.freeze_now': 'Froze a wallet',
  'bills.catalog': 'Viewed billers',
};

/** "money.pool_checks" -> "Viewed money: pool checks", for actions without a name above. */
export function actionLabel(action: string): string {
  const known = LABELS[action];
  if (known) return known;
  const [area, ...rest] = action.split('.');
  const what = rest.join(' ').replaceAll('_', ' ');
  return area === 'money' ? `Viewed money: ${what}` : `${what} ${area}`.trim();
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MINOR_KEY = /[ _]?minor$/i;

function humanKey(key: string): string {
  const spaced = key.replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function humanValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    if (UUID.test(value)) return value.slice(0, 8);
    return value.length > 60 ? `${value.slice(0, 57)}…` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  const text = readableValue('', value);
  if (!text) return null;
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}

export interface DetailPair {
  label: string;
  value: string;
}

/**
 * The entry's customer and detail as named fields. A staff id shows the
 * member's name when it is known; other ids are cut to their first 8 characters.
 */
export function detailPairs(
  detail: Record<string, unknown>,
  customerId: string | null,
  staffNames: ReadonlyMap<string, string>,
): DetailPair[] {
  const pairs: DetailPair[] = [];
  if (customerId) {
    pairs.push({ label: 'Customer', value: customerId.slice(0, 8) });
  }
  for (const [key, raw] of Object.entries(detail)) {
    const isStaff = key === 'staff_id' && typeof raw === 'string';
    const value = isStaff
      ? (staffNames.get(raw as string) ?? humanValue(raw))
      : humanValue(raw);
    if (value === null) continue;
    pairs.push({ label: isStaff ? 'Staff' : humanKey(key), value });
  }
  return pairs.slice(0, 6);
}

/** Any stored detail as named fields a person can read, never raw JSON. */
export function readablePairs(value: unknown): DetailPair[] {
  if (value === null || value === undefined) return [];
  if (typeof value !== 'object' || Array.isArray(value)) {
    const only = humanValue(value);
    return only === null ? [] : [{ label: 'Detail', value: only }];
  }
  return Object.entries(value).flatMap(([key, raw]) => {
    // Money in pesewas ("amount_minor": 564) reads as cedis: "Amount 5.64".
    if (typeof raw === 'number' && MINOR_KEY.test(key)) {
      return [{ label: readableLabel(key), value: readableValue(key, raw) }];
    }
    const shown = humanValue(raw);
    return shown === null ? [] : [{ label: humanKey(key), value: shown }];
  });
}

/** "wallet_created" -> "Wallet created". */
export function humanWords(value: string): string {
  return humanKey(value);
}

export interface ActionOption {
  value: string;
  label: string;
}

/**
 * The actions the log has words for, for the Action filter: each area as
 * "Everything in staff" (the server matches an area as a prefix), then its
 * actions in plain words. Areas and actions are in alphabetical order.
 */
export function actionOptions(): ActionOption[] {
  const byArea = new Map<string, string[]>();
  for (const action of Object.keys(LABELS)) {
    const area = action.split('.')[0] ?? action;
    byArea.set(area, [...(byArea.get(area) ?? []), action]);
  }
  return [...byArea.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([area, actions]) => [
      { value: area, label: `Everything in ${area.replaceAll('_', ' ')}` },
      ...actions
        .map((action) => ({
          value: action,
          label: `${area.replaceAll('_', ' ')}: ${actionLabel(action)}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ]);
}

export type AuditSubject = {
  type: 'cust' | 'tx' | 'issue' | 'change';
  id: string;
} | null;

const SUBJECT_KEYS = [
  ['change_request_id', 'change'],
  ['issue_id', 'issue'],
  ['transaction_id', 'tx'],
] as const;

/**
 * The record an entry is about, when it names one: its customer, or a
 * change request, issue report or transaction in its detail.
 */
export function auditSubject(entry: {
  customer_id?: string | null;
  detail: Record<string, unknown>;
}): AuditSubject {
  for (const [key, type] of SUBJECT_KEYS) {
    const id = entry.detail[key];
    if (typeof id === 'string' && UUID.test(id)) return { type, id };
  }
  return entry.customer_id ? { type: 'cust', id: entry.customer_id } : null;
}

/**
 * An entry's detail as named fields for the sheet: every field, a staff id as
 * the member's name when it is known.
 */
export function entryPairs(
  detail: Record<string, unknown>,
  staffNames: ReadonlyMap<string, string>,
): DetailPair[] {
  const named = Object.fromEntries(
    Object.entries(detail).map(([key, value]) => [
      key,
      typeof value === 'string' && staffNames.has(value)
        ? staffNames.get(value)
        : value,
    ]),
  );
  return readablePairs(named);
}
